import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
  Dispatch,
  SetStateAction,
  useMemo,
} from 'react';

import { BoardSteps } from 'utils/constants';
import useUser from 'api/hooks/useUser';
import useGetMetric from 'hooks/useGetMetric';
import useCroptimizerProduct from 'api/hooks/useCroptimizerProduct';
import useCroptimizerEfficiency from 'api/hooks/useCroptimizerEfficiency';
import { EfficiencyProps, ProductsProps } from 'utils/types';

import { mixpanelTrack } from '../utils/constants';
import {
  SettingCropProps,
  FieldProps,
  BoardStatus,
  BoardSoil,
  BoardCrop,
  BoardFertilization,
  BoardMineralization,
} from '../types';

interface CroptimizerContextProps {
  activeTab: number;
  setActiveTab: Dispatch<SetStateAction<number>>;
  crops: SettingCropProps[];
  setCrops: Dispatch<SetStateAction<SettingCropProps[]>>;
  step: BoardSteps;
  setStep: Dispatch<SetStateAction<BoardSteps>>;
  fields: FieldProps[];
  setFields: Dispatch<SetStateAction<FieldProps[]>>;
  activeTabCrop: number;
  setActiveTabCrop: Dispatch<SetStateAction<number>>;
  disabledState: string[];
  setDisabledState: Dispatch<SetStateAction<string[]>>;
  farm: string;
  setFarm: Dispatch<SetStateAction<string>>;
  startDate: Date | null;
  setStartDate: Dispatch<SetStateAction<Date | null>>;
  boardStatusValues: BoardStatus | null;
  setBoardStatusValues: Dispatch<SetStateAction<BoardStatus | null>>;
  boardSoilValues: BoardSoil | null;
  setBoardSoilValues: Dispatch<SetStateAction<BoardSoil | null>>;
  boardCropValues: BoardCrop | null;
  setBoardCropValues: Dispatch<SetStateAction<BoardCrop | null>>;
  fertilizationValues: BoardFertilization | null;
  setFertilizationValues: Dispatch<SetStateAction<BoardFertilization | null>>;
  mineralizationValues: BoardMineralization | null;
  setMineralizationValues: Dispatch<SetStateAction<BoardMineralization | null>>;
  user: any; // TODO: add type
  ton: string;
  kgHa: string;
  kg: string;
  mtHa: string;
  onlyMt: string;
  onlyAc: string;
  metricValue: string;
  products: ProductsProps[] | undefined;
  efficiency: EfficiencyProps[] | undefined;
  isExpertMode: boolean;
  setIsExpertMode: (value: boolean) => void;
  isMaintenanceMode: boolean;
}

const CroptimizerContext = createContext<CroptimizerContextProps | undefined>(
  undefined,
);

export const CroptimizerProvider = ({ children }: { children: ReactNode }) => {
  const { data: user } = useUser();
  const { metricValue, kg, mtHa, ton, kgHa, onlyMt, onlyAc } = useGetMetric();
  const { data: products } = useCroptimizerProduct();
  const { data: efficiency } = useCroptimizerEfficiency();

  // All your state from CroptimizerContent
  const [activeTab, setActiveTab] = useState<number>(1);
  const [crops, setCrops] = useState<SettingCropProps[]>([]);
  const [step, setStep] = useState(BoardSteps.status);
  const [fields, setFields] = useState<FieldProps[]>([]);
  const [activeTabCrop, setActiveTabCrop] = useState<number>(1);
  const [disabledState, setDisabledState] = useState<string[]>([]);
  const [farm, setFarm] = useState<string>('');
  const [startDate, setStartDate] = useState<Date | null>(new Date());
  const [boardStatusValues, setBoardStatusValues] =
    useState<BoardStatus | null>(null);
  const [boardSoilValues, setBoardSoilValues] = useState<BoardSoil | null>(
    null,
  );
  const [boardCropValues, setBoardCropValues] = useState<BoardCrop | null>(
    null,
  );
  const [fertilizationValues, setFertilizationValues] =
    useState<BoardFertilization | null>(null);
  const [mineralizationValues, setMineralizationValues] =
    useState<BoardMineralization | null>(null);
  const [isExpertMode, setIsExpertModeState] = useState<boolean>(
    () => localStorage.getItem('isSwitcherEnabled') === 'true',
  );

  const setIsExpertMode = (value: boolean) => {
    localStorage.setItem('isSwitcherEnabled', value.toString());
    setIsExpertModeState(value);
  };

  const isMaintenanceMode = useMemo(() => {
    if (boardStatusValues?.currentPh && boardStatusValues?.targetPh) {
      return Number(boardStatusValues.currentPh) === Number(boardStatusValues.targetPh);
    }

    return false;
  }, [boardStatusValues?.currentPh, boardStatusValues?.targetPh]);

  useEffect(() => {
    const trackProps = {
      role: user?.role?.name,
      organization: user?.organization,
    };

    mixpanelTrack(activeTab, step, trackProps);
  }, [step, activeTab]);

  useEffect(() => {
    if (!activeTabCrop && fields.length) {
      setActiveTabCrop(fields[0].id);
    }
  }, [fields.length]);

  if (!metricValue) {
    return null;
  }

  return (
    <CroptimizerContext.Provider
      value={{
        activeTab,
        setActiveTab,
        crops,
        setCrops,
        step,
        setStep,
        fields,
        setFields,
        activeTabCrop,
        setActiveTabCrop,
        disabledState,
        setDisabledState,
        farm,
        setFarm,
        startDate,
        setStartDate,
        boardStatusValues,
        setBoardStatusValues,
        boardSoilValues,
        setBoardSoilValues,
        boardCropValues,
        setBoardCropValues,
        fertilizationValues,
        setFertilizationValues,
        mineralizationValues,
        setMineralizationValues,
        user,
        metricValue,
        kg,
        mtHa,
        ton,
        kgHa,
        onlyMt,
        onlyAc,
        products,
        efficiency,
        isExpertMode,
        setIsExpertMode,
        isMaintenanceMode,
      }}
    >
      {children}
    </CroptimizerContext.Provider>
  );
};

export const useCroptimizer = () => {
  const context = useContext(CroptimizerContext);

  if (!context) {
    throw new Error('useCroptimizer must be used within a CroptimizerProvider');
  }

  return context;
};
